import React, { useEffect, useState } from 'react';
import Hero from '../components/Hero';
import FeaturedBrands from '../components/FeaturedBrands';
import CategorySection from '../components/CategorySection';
import Newsletter from '../components/Newsletter';
import CustomerReviews from '../components/CustomerReviews';
import ProductRecommendations from '../components/ProductRecommendations';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Truck, Shield, RotateCcw, Clock, Calendar, Archive, RefreshCw, Search, Loader2 } from 'lucide-react';
import { useProducts } from '../hooks/useProducts';
import ProductCard from '../components/ProductCard';

const HomePage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  
  // Fetch products with search and category filters
  const { products, loading, error } = useProducts({ 
    category: selectedCategory || undefined,
    search: searchQuery || undefined,
    limit: 12
  });

  useEffect(() => {
    // Update the document title
    document.title = "Strum Music | Premium Music Instruments & Accessories";
  }, []);

  const categories = [
    { id: '', name: 'All Products' },
    { id: 'guitars', name: 'Guitar' },
    { id: 'keyboards', name: 'Keyboard' },
    { id: 'studio', name: 'Studio' },
    { id: 'accessories', name: 'Accessories' }
  ];

  return (
    <div>
      <Hero />
      
      {/* Featured Categories */}
      <CategorySection />
      
      {/* Benefits Bar */}
      <section className="py-8 bg-white border-y border-gray-100">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: <Truck />, title: "Free Shipping", description: "On orders over ₹9,999" },
              { icon: <Shield />, title: "2-Year Warranty", description: "On all instruments" },
              { icon: <RotateCcw />, title: "Easy Returns", description: "30-day return policy" },
              { icon: <Clock />, title: "Expert Support", description: "Available 7 days a week" }
            ].map((benefit, index) => (
              <motion.div 
                key={index}
                className="flex items-center space-x-3"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="flex-shrink-0 text-red-700">
                  {benefit.icon}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-black">{benefit.title}</h3>
                  <p className="text-xs text-gray-500">{benefit.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
            
      {/* Featured Brands */}
      <FeaturedBrands />
      
      {/* Customer Reviews */}
      <CustomerReviews limit={6} />
      
      {/* Product Recommendations */}
      <ProductRecommendations 
        title="Trending Products"
        limit={8}
      />
      
      {/* Product Search and Filter Section */}
      <section className="py-12 bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-8">
            <motion.h2 
              className="text-3xl font-bold text-black"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              Browse Our Products
            </motion.h2>
            <motion.p 
              className="mt-2 text-gray-600 max-w-2xl mx-auto"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Search and filter through our extensive collection of musical instruments
            </motion.p>
          </div>

          {/* Search Bar */}
          <motion.div 
            className="mb-6 max-w-md mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
              <input 
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white py-3 pl-10 pr-4 text-gray-900 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-red-700 focus:border-red-700 transition-colors"
              />
            </div>
          </motion.div>

          {/* Category Filter Buttons */}
          <motion.div 
            className="mb-8 flex flex-wrap justify-center gap-2"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
          >
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-red-700 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-red-50 hover:text-red-700 hover:border-red-300'
                }`}
              >
                {category.name}
              </button>
            ))}
          </motion.div>

          {/* Loading Spinner */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-red-700" />
              <span className="ml-2 text-gray-600">Loading products...</span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="text-center py-12">
              <p className="text-red-600">Error loading products: {error}</p>
            </div>
          )}

          {/* Products Grid */}
          {!loading && !error && (
            <>
              {products.length > 0 ? (
                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                  viewport={{ once: true }}
                >
                  {products.map((product, index) => (
                    <motion.div
                      key={product.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.05 }}
                      viewport={{ once: true }}
                    >
                      <ProductCard product={product} />
                    </motion.div>
                  ))}
                </motion.div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-600">
                    {searchQuery || selectedCategory 
                      ? 'No products found matching your criteria.' 
                      : 'No products available at the moment.'
                    }
                  </p>
                  {(searchQuery || selectedCategory) && (
                    <button 
                      onClick={() => {
                        setSearchQuery('');
                        setSelectedCategory('');
                      }}
                      className="mt-4 btn btn-outline py-2 px-4"
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              )}

              {/* View All Products Link */}
              {products.length > 0 && (
                <div className="text-center mt-8">
                  <Link 
                    to="/products" 
                    className="btn btn-primary px-6 py-3"
                  >
                    View All Products
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      </section>
      
      {/* Promotional Banner */}
      <section className="py-16 bg-black text-white overflow-hidden relative">
        <div className="absolute inset-0 z-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 to-black/30"></div>
          <img 
            src="https://images.pexels.com/photos/164746/pexels-photo-164746.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
            alt="Music instruments" 
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-3 py-1 mb-4 text-xs font-semibold bg-red-700 text-white rounded-full">
                Special Offer
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Summer Sale! Up to 30% off on Selected Instruments
              </h2>
              <p className="text-gray-300 mb-6">
                Upgrade your musical setup with our premium selection of instruments and accessories. 
                Limited-time offer valid while supplies last.
              </p>
              <a 
                href="#" 
                className="inline-block bg-red-700 hover:bg-red-800 text-white font-medium px-6 py-3 rounded-md transition-colors"
              >
                Shop the Sale
              </a>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Newsletter */}
      <Newsletter />
    </div>
  );
};

export default HomePage;